<template>
  <!-- 无UI纯功能组件，保持原组件结构 -->
  <div></div>
</template>

<script>
import {Message, Notification } from 'element-ui';

export default {
  name: 'DanceToolFileOperator',
  props: {
    // 移除插件相关配置（不再依赖插件）
    // 是否自动执行浏览器兼容性检测（检测File System Access API支持）
    autoDetect: {
      type: Boolean,
      default: true
    },
    // 检测成功是否显示提示
    showSuccessNotice: {
      type: Boolean,
      default: false
    },
    // 文件操作默认编码（API读文本默认utf-8，其他编码需额外处理）
    fileEncoding: {
      type: String,
      default: 'utf-8',
      validator: (val) => ['utf-8', 'utf-16', 'gbk'].includes(val) // 限制支持的编码
    }
  },
  data() {
    return {
      // 检测状态：从"插件检测"改为"API兼容性检测"
      detectStatus: {
        isApiSupported: false, // 是否支持File System Access API
        matchedBrowser: null,  // 匹配的兼容浏览器（Chrome/Edge）
        completed: false       // 检测是否完成
      },
      // 浏览器配置：保留Chrome/Edge（仅这两类浏览器完善支持API）
      browserConfigs: [
        {
          id: 'chrome',
          name: 'Google Chrome',
          detect: (userAgent, vendor) => {
            const lowerUA = userAgent.toLowerCase();
            return lowerUA.includes('chrome') && !lowerUA.includes('edg') && vendor.toLowerCase().includes('google');
          },
          browserUrl: 'https://www.google.com/chrome/' // 浏览器下载地址
        },
        {
          id: 'edge',
          name: 'Microsoft Edge',
          detect: (userAgent) => {
            const lowerUA = userAgent.toLowerCase();
            return lowerUA.includes('edg');
          },
          browserUrl: 'https://www.microsoft.com/zh-cn/edge'
        }
      ],
      // 文件状态：从"插件文件ID"改为"API文件句柄"（核心变更）
      fileState: {
        fileHandle: null,      // 选中文件的FileSystemFileHandle
        dirHandle: null,       // 选中目录的FileSystemDirectoryHandle（新增，用于路径关联）
        lastContent: '',       // 上次读取的文件内容（保留原逻辑）
        fileMeta: null         // 文件元数据（名称、大小、修改时间等）
      }
    };
  },
  mounted() {
    // 自动触发API兼容性检测（替代原插件检测）
    if (this.autoDetect) {
      this.startDetection();
    }
  },
  methods: {
    // ========================== 基础能力：API兼容性检测 ==========================
    /**
     * 手动触发浏览器+API兼容性检测
     * @returns {Promise<Object>} 检测结果
     */
    startDetection() {
      return new Promise((resolve) => {
        this.detectStatus.completed = false;
        // 步骤1：检测浏览器类型
        this._matchBrowser()
            .then(() => {
              // 步骤2：检测File System Access API支持
              this._checkApiSupport();
              this.detectStatus.completed = true;
              this.$emit('detect-update', { ...this.detectStatus });
              resolve({ ...this.detectStatus });
            });
      });
    },

    /**
     * 私有方法：匹配兼容的浏览器（Chrome/Edge）
     */
    _matchBrowser() {
      return new Promise((resolve) => {
        const userAgent = navigator.userAgent.toLowerCase();
        const vendor = navigator.vendor ? navigator.vendor.toLowerCase() : '';

        // 匹配浏览器配置中的兼容类型
        this.detectStatus.matchedBrowser = this.browserConfigs.find(browser =>
            browser.detect(userAgent, vendor)
        );
        resolve();
      });
    },

    /**
     * 私有方法：检测File System Access API支持性
     * 核心API：showOpenFilePicker、showSaveFilePicker、FileSystemHandle等
     */
    _checkApiSupport() {
      // 检测核心API是否存在（仅Chromium内核浏览器支持）
      this.detectStatus.isApiSupported = 'showOpenFilePicker' in window &&
          'FileSystemFileHandle' in window &&
          'createWritable' in FileSystemFileHandle.prototype;

      // API不支持时的提示逻辑
      if (!this.detectStatus.isApiSupported) {
        const browserList = this.browserConfigs.map(b => `<li>${b.name}（90+版本）</li>`).join('');
        this.$confirm(
            `<p>当前浏览器不支持本地文件操作API，请使用以下浏览器的最新版本：</p>
           <ul style="margin:10px 0 0 20px;">${browserList}</ul>
           <p class="text-warning">注：API仅支持HTTPS/localhost环境，HTTP环境无法使用</p>`,
            'API不兼容',
            {
              dangerouslyUseHTMLString: true,
              confirmButtonText: '关闭',
              cancelButtonText: '下载推荐浏览器',
              type: 'warning'
            }
        ).catch(() => {
          // 打开第一个推荐浏览器的下载页
          window.open(this.browserConfigs[0].browserUrl, '_blank');
        });
      } else if (this.showSuccessNotice) {
        // API支持时的成功提示
        Message.success({
          title: 'API就绪',
          message: '当前浏览器支持本地文件操作，可正常使用所有功能'
        });
        this.$emit('api-ready'); // 替代原插件的plugin-ready事件
      }
    },

    // ========================== 核心能力：File System Access API实现 ==========================
    /**
     * 1. 选择本地文件（替代原插件SELECT_FILE）
     * @param {Array<String>} accept - 允许选择的文件类型（如['.txt', '.json']）
     * @returns {Promise<Object>} 包含文件句柄和元数据的对象
     */
    selectFile(accept = ['.txt', '.json', '.js']) {
      return this._checkOperatePermission().then(() => {
        return new Promise(async (resolve, reject) => {
          try {
            // 1. 转换accept格式：适配API要求的{ MIME类型: [后缀名] }结构
            const apiAccept = {};
            accept.forEach(ext => {
              const mime = this._getMimeTypeByExt(ext);
              if (!apiAccept[mime]) apiAccept[mime] = [];
              apiAccept[mime].push(ext);
            });

            // 2. 调用API打开文件选择器
            const [fileHandle] = await window.showOpenFilePicker({
              types: [{ description: '支持的文件', accept: apiAccept }],
              multiple: false, // 仅选单个文件
              excludeAcceptAllOption: true // 不显示"所有文件"选项
            });

            // 3. 获取文件元数据（名称、大小、修改时间等）
            const file = await fileHandle.getFile();
            console.log('file:', file)
            const fileMeta = {
              name: file.name,
              size: file.size,
              lastModified: file.lastModified,
              lastModifiedDate: file.lastModifiedDate.toLocaleString(),
              type: file.type
            };

            // 4. 更新状态并返回结果
            this.fileState.fileHandle = fileHandle;
            this.fileState.fileMeta = fileMeta;
            this.fileState.lastContent = ''; // 重置上次内容

            Notification.success({
              title: '文件选择成功',
              message: `已选中：${fileMeta.name}（${(fileMeta.size/1024).toFixed(2)}KB）`
            });

            const result = { fileHandle, fileMeta };
            this.$emit('file-selected', result); // 保持原事件名
            resolve(result);
          } catch (err) {
            // 处理用户取消选择或权限错误
            if (err.name === 'AbortError') {
              reject(new Error('用户取消了文件选择'));
            } else {
              Message.error(`文件选择失败：${err.message || '未知错误'}`);
              reject(new Error(`文件选择失败：${err.message}`));
            }
          }
        });
      });
    },

    /**
     * 2. 读取文件内容（替代原插件READ_FILE）
     * @param {FileSystemFileHandle} [fileHandle] - 可选：文件句柄（默认用当前选中文件）
     * @returns {Promise<String>} 文件内容
     */
    readFile(fileHandle) {
      return this._checkOperatePermission().then(() => {
        // 优先使用传入的句柄，无则用当前选中的句柄
        const targetHandle = fileHandle || this.fileState.fileHandle;
        if (!targetHandle) {
          Message.warning('请先选择文件');
          return Promise.reject(new Error('未选中文件'));
        }

        return new Promise(async (resolve, reject) => {
          try {
            // 1. 通过句柄获取File对象
            const file = await targetHandle.getFile();

            // 2. 读取文件内容（处理编码：API默认utf-8，其他编码需额外处理）
            let content;
            if (this.fileEncoding === 'utf-8') {
              content = await file.text(); // 原生支持utf-8
            } else {
              // 非utf-8编码（如gbk）：需借助TextDecoder，浏览器需支持对应的编码
              const arrayBuffer = await file.arrayBuffer();
              const decoder = new TextDecoder(this.fileEncoding);
              content = decoder.decode(arrayBuffer);
            }

            // 3. 更新状态并返回结果
            this.fileState.lastContent = content;
            this.$emit('file-read', {
              fileMeta: this.fileState.fileMeta,
              content
            }); // 保持原事件名

            resolve(content);
          } catch (err) {
            Message.error(`文件读取失败：${err.message || '编码不支持或文件损坏'}`);
            reject(new Error(`文件读取失败：${err.message}`));
          }
        });
      });
    },

    /**
     * 3. 写入文件（全量覆盖，替代原插件WRITE_FILE）
     * @param {Object} options - 写入配置
     * @param {String} options.content - 要写入的内容
     * @param {FileSystemFileHandle} [options.fileHandle] - 可选：文件句柄（默认当前选中文件）
     * @param {Boolean} [options.backup] - 可选：是否备份原内容（默认true）
     * @returns {Promise<Boolean>} 写入结果
     */
    writeFile({ content, fileHandle, backup = true }) {
      return this._checkOperatePermission().then(() => {
        const targetHandle = fileHandle || this.fileState.fileHandle;
        if (!targetHandle) {
          Message.warning('请先选择文件');
          return Promise.reject(new Error('未选中文件'));
        }
        if (content === undefined || content === null) {
          Message.warning('写入内容不能为空');
          return Promise.reject(new Error('写入内容为空'));
        }

        return new Promise(async (resolve, reject) => {
          let originalContent = '';
          try {
            // 1. 备份原内容（如需回滚）
            if (backup) {
              const file = await targetHandle.getFile();
              originalContent = await file.text();
            }

            // 2. 创建可写流并写入内容
            const writable = await targetHandle.createWritable({
              keepExistingData: false // 全量覆盖（false=覆盖，true=追加）
            });

            // 处理编码：默认utf-8，其他编码需转ArrayBuffer
            if (this.fileEncoding === 'utf-8') {
              await writable.write(content);
            } else {
              const encoder = new TextEncoder(this.fileEncoding);
              await writable.write(encoder.encode(content));
            }

            await writable.close(); // 关闭流完成写入

            // 3. 更新状态并返回结果
            this.fileState.lastContent = content;
            // 刷新文件元数据（大小可能变化）
            const updatedFile = await targetHandle.getFile();
            this.fileState.fileMeta.size = updatedFile.size;
            this.fileState.fileMeta.lastModified = updatedFile.lastModified;

            Message.success('文件写入成功');
            this.$emit('file-written', {
              fileMeta: this.fileState.fileMeta,
              content
            }); // 保持原事件名

            resolve(true);
          } catch (err) {
            // 写入失败时回滚原内容（仅备份开启时）
            if (backup && originalContent) {
              try {
                const writable = await targetHandle.createWritable();
                await writable.write(originalContent);
                await writable.close();
                Message.info('写入失败，已自动回滚原内容');
              } catch (rollbackErr) {
                Message.error(`写入失败且回滚出错：${rollbackErr.message}`);
              }
            }

            Message.error(`文件写入失败：${err.message || '权限不足或文件被占用'}`);
            reject(new Error(`文件写入失败：${err.message}`));
          }
        });
      });
    },

    /**
     * 4. 按行替换文件内容（核心功能，逻辑与原组件一致）
     * @param {Object} options - 替换配置
     * @param {Number} options.startLine - 开始行号（1-based）
     * @param {Number} options.endLine - 结束行号（1-based）
     * @param {String|Array<String>} options.replaceContent - 替换内容（字符串/行数组）
     * @param {FileSystemFileHandle} [options.fileHandle] - 可选：文件句柄
     * @returns {Promise<Object>} 替换结果
     */
    replaceFileByLine({ startLine, endLine, replaceContent, fileHandle }) {
      return this._checkOperatePermission().then(() => {
        const targetHandle = fileHandle || this.fileState.fileHandle;
        if (!targetHandle) {
          Message.warning('请先选择文件');
          return Promise.reject(new Error('未选中文件'));
        }
        if (startLine > endLine || startLine < 1) {
          Message.warning(`行号范围无效：开始行（${startLine}）需≥1且≤结束行（${endLine}）`);
          return Promise.reject(new Error('行号范围无效'));
        }

        // 先读再写：复用readFile和writeFile方法
        return this.readFile(targetHandle).then((currentContent) => {
          // 1. 拆分内容为行数组（兼容Windows/macOS换行符）
          const lines = currentContent.split(/\r?\n/);
          const originalLines = [...lines]; // 备份原行
          const originalContent = currentContent;

          // 2. 处理替换内容（统一转为数组）
          const replaceLines = Array.isArray(replaceContent)
              ? replaceContent
              : replaceContent.split(/\r?\n/);

          // 3. 执行行替换（slice是0-based，行号是1-based，需偏移）
          lines.splice(startLine - 1, endLine - startLine + 1, ...replaceLines);
          const newContent = lines.join('\n');

          // 4. 写入替换后的内容
          return this.writeFile({
            fileHandle: targetHandle,
            content: newContent,
            backup: true
          }).then(() => {
            const result = {
              fileMeta: this.fileState.fileMeta,
              originalContent,
              newContent,
              originalLines,
              replacedLines: replaceLines,
              startLine,
              endLine
            };

            Notification.success({
              title: '行替换成功',
              message: `已替换第${startLine}-${endLine}行（共${replaceLines.length}行）`
            });
            this.$emit('file-line-replaced', result); // 保持原事件名
            return result;
          });
        });
      });
    },

    /**
     * 5. 删除文件（替代原插件DELETE_FILE）
     * @param {FileSystemFileHandle} [fileHandle] - 可选：文件句柄
     * @returns {Promise<Boolean>} 删除结果
     */
    deleteFile(fileHandle) {
      return this._checkOperatePermission().then(() => {
        const targetHandle = fileHandle || this.fileState.fileHandle;
        if (!targetHandle) {
          Message.warning('请先选择文件');
          return Promise.reject(new Error('未选中文件'));
        }

        // 二次确认（危险操作）
        return this.$confirm(
            `确定要删除文件"${this.fileState.fileMeta?.name || '未知文件'}"吗？此操作不可恢复！`,
            '危险操作',
            {
              confirmButtonText: '确认删除',
              cancelButtonText: '取消',
              type: 'error'
            }
        ).then(async () => {
          try {
            // 调用API删除文件（需目录句柄或通过文件句柄的remove方法）
            if ('remove' in targetHandle) {
              await targetHandle.remove(); // 直接删除文件（部分浏览器支持）
            } else if (this.fileState.dirHandle) {
              // 部分浏览器需通过目录句柄删除文件
              await this.fileState.dirHandle.removeEntry(targetHandle.name);
            } else {
              // 无目录句柄时，提示用户先选择目录（兼容处理）
              throw new Error('需先选择文件所在目录才能执行删除，请重新选择文件');
            }

            // 重置状态
            this.fileState.fileHandle = null;
            this.fileState.fileMeta = null;
            this.fileState.lastContent = '';

            Message.success('文件删除成功');
            this.$emit('file-deleted', {
              fileName: this.fileState.fileMeta?.name || '未知文件'
            }); // 保持原事件名

            return true;
          } catch (err) {
            Message.error(`文件删除失败：${err.message || '权限不足或文件被占用'}`);
            return Promise.reject(new Error(`文件删除失败：${err.message}`));
          }
        });
      });
    },

    /**
     * 6. 创建新文件（替代原插件CREATE_FILE，适配API逻辑）
     * @param {Object} options - 创建配置
     * @param {String} options.fileName - 文件名（含后缀，如"test.txt"）
     * @param {String} [options.initialContent] - 初始内容（默认空）
     * @returns {Promise<Object>} 新文件的句柄和元数据
     */
    createFile({ fileName, initialContent = '' }) {
      return this._checkOperatePermission().then(() => {
        // 校验文件名（必须含后缀）
        if (!fileName || !fileName.includes('.')) {
          Message.error('文件名无效，请包含后缀（如"test.txt"）');
          return Promise.reject(new Error('文件名无效'));
        }

        return new Promise(async (resolve, reject) => {
          try {
            // 1. 调用API打开"保存文件"对话框（让用户选择保存路径）
            const fileHandle = await window.showSaveFilePicker({
              suggestedName: fileName,
              types: [{
                description: '新文件',
                accept: {
                  [this._getMimeTypeByExt(fileName.slice(fileName.lastIndexOf('.')))]:
                      [fileName.slice(fileName.lastIndexOf('.'))]
                }
              }],
              excludeAcceptAllOption: true
            });

            // 2. 写入初始内容
            const writable = await fileHandle.createWritable();
            await writable.write(initialContent);
            await writable.close();

            // 3. 获取新文件的元数据
            const file = await fileHandle.getFile();
            const fileMeta = {
              name: file.name,
              size: file.size,
              lastModified: file.lastModified,
              lastModifiedDate: file.lastModifiedDate.toLocaleString(),
              type: file.type
            };

            // 4. 更新状态并返回结果
            this.fileState.fileHandle = fileHandle;
            this.fileState.fileMeta = fileMeta;
            this.fileState.lastContent = initialContent;

            // 尝试获取目录句柄（用于后续删除等操作）
            const dirPath = fileMeta.name.includes('/')
                ? fileMeta.name.slice(0, fileMeta.name.lastIndexOf('/'))
                : '.';
            if (dirPath !== '.') {
              this.fileState.dirHandle = await window.showDirectoryPicker({
                startIn: dirPath
              });
            }

            Message.success(`文件创建成功：${fileMeta.name}`);
            this.$emit('file-created', { fileHandle, fileMeta }); // 保持原事件名

            resolve({ fileHandle, fileMeta });
          } catch (err) {
            if (err.name === 'AbortError') {
              reject(new Error('用户取消了文件创建'));
            } else {
              Message.error(`文件创建失败：${err.message || '权限不足'}`);
              reject(new Error(`文件创建失败：${err.message}`));
            }
          }
        });
      });
    },

    // ========================== 辅助工具方法 ==========================
    /**
     * 私有方法：检查文件操作的前置条件（API支持+文件句柄）
     * @returns {Promise<void>}
     */
    _checkOperatePermission() {
      return new Promise((resolve, reject) => {
        // 1. 检查API是否支持
        if (!this.detectStatus.isApiSupported) {
          const errMsg = '当前环境不支持文件操作API，请检查浏览器和环境（HTTPS/localhost）';
          Message.error(errMsg);
          return reject(new Error(errMsg));
        }

        // 2. 检查HTTPS/localhost环境（API强制要求）
        if (window.location.protocol !== 'https:' && window.location.hostname !== 'localhost') {
          const errMsg = '文件操作API仅支持HTTPS或localhost环境，HTTP环境无法使用';
          Message.error(errMsg);
          return reject(new Error(errMsg));
        }

        resolve();
      });
    },

    /**
     * 私有方法：通过文件后缀名获取MIME类型（适配API的accept格式）
     * @param {String} ext - 文件后缀（如".txt"）
     * @returns {String} MIME类型
     */
    _getMimeTypeByExt(ext) {
      const mimeMap = {
        '.txt': 'text/plain',
        '.json': 'application/json',
        '.js': 'application/javascript',
        '.css': 'text/css',
        '.html': 'text/html',
        '.md': 'text/markdown'
      };
      return mimeMap[ext.toLowerCase()] || 'application/octet-stream';
    },

    /**
     * 重置组件状态（保留原方法逻辑）
     */
    resetState() {
      this.fileState = {
        fileHandle: null,
        dirHandle: null,
        lastContent: '',
        fileMeta: null
      };
      this.detectStatus = {
        isApiSupported: false,
        matchedBrowser: null,
        completed: false
      };
      this.$emit('state-reset');
    }
  },
  // 暴露方法给父组件（保持原provide结构，确保调用兼容性）
  provide() {
    return {
      danceToolFileOperator: {
        startDetection: this.startDetection,
        selectFile: this.selectFile,
        readFile: this.readFile,
        writeFile: this.writeFile,
        replaceFileByLine: this.replaceFileByLine,
        deleteFile: this.deleteFile,
        createFile: this.createFile,
        resetState: this.resetState
      }
    };
  }
};
</script>

<style scoped></style>